import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { RowLayout } from '../../../common/layouts/rowLayout/RowLayout';
import { IChartPoint, ITrainingMatrix, ITrainingResult } from '../../jobMetrics.model';
import { IValidJobType, JOB_STATUS, JOB_TYPE } from '../../jobs.model';
import { IJobThreshold } from '../../jobThreshold.store';
import { ConfusionMatrix } from './contents/ConfusionMatrix';
import { LossMetrics } from './contents/LossMetrics';
import { MetricsBase } from './contents/MetricsBase';
import { ResultsTable } from './contents/ResultsTable';

interface IProps {
  jobType: IValidJobType;
  loss?: IChartPoint[];
  validationAccuracy?: IChartPoint[];
  trainingAccuracy?: IChartPoint[];
  matrix: ITrainingMatrix;
  results: ITrainingResult[];
  recalculateMetrics?: (min: number, max: number) => void;
  threshold?: IJobThreshold | null;
  status: JOB_STATUS;
}

export const ObjectClassification = observer((props: IProps): ReactElement => {
  const { jobType, loss, validationAccuracy, trainingAccuracy, recalculateMetrics, matrix, results, threshold, status } = props;

  const lossMetrics = useMemo(
    () =>
      loss &&
      validationAccuracy &&
      trainingAccuracy && (
        <LossMetrics
          loss={loss}
          validationAccuracy={validationAccuracy}
          trainingAccuracy={trainingAccuracy}
          status={status}
        />
      ),
    [loss, validationAccuracy, trainingAccuracy]
  );

  return (
    <>
      {jobType === JOB_TYPE.TRAINING && lossMetrics}
      <MetricsBase
        jobType={jobType}
        recalculateMetrics={recalculateMetrics}
        threshold={threshold}
        status={status}
      >
        <RowLayout
          size='l'
          className='metrics-row-base'
        >
          <ColumnLayout size='md'>
            <ConfusionMatrix
              matrix={matrix}
              jobType={jobType}
            />
          </ColumnLayout>
          <ColumnLayout
            size='md'
            className={classNames({ 'smaller-gap': results.length !== 0 })}
          >
            <ResultsTable
              results={results}
              jobType={jobType}
            />
          </ColumnLayout>
        </RowLayout>
      </MetricsBase>
    </>
  );
});
